// A lightweight convenience wrapper for the fetch to handle security credentials.
// This is just to centralize some of the common boilerplate for reuse.
import { NATIVE_CLONE, NATIVE_FETCH, NATIVE_JSON, NATIVE_TEXT } from './nativeRefs';

async function handleFetch({ url, method, success, error, payload, headers, }: {url: string, method: string, success?: Function, error?: Function, payload?: object, headers?: object}): Promise<any> {
  let options = {
    method: method,
    cors: 'cors',
    credentials: 'include',
    headers: addCsrfHeaders({
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...headers,
    })
  }

  if (payload) {
    options['body'] = JSON.stringify(payload)
  }

  const response = await NATIVE_FETCH(fullyQualifiedUrl(url), options)
  const textResponse = await NATIVE_TEXT.call(NATIVE_CLONE.call(response))

  let jsonResponse = {};
  if (textResponse.length) {
    try {
      jsonResponse = await NATIVE_JSON.call(NATIVE_CLONE.call(response));
    } catch (e) {
      // Response is not valid JSON, keep jsonResponse as empty object
      // This handles cases where backend returns plain text like "OK"
    }
  }

  if (response.status === 303 && jsonResponse.redirect) {
    window.location.href = jsonResponse.redirect
  } else if (response.ok && success) {
    success(jsonResponse)
  } else if (!response.ok && error) {
    const jsonResponseWithErrorStatus = { ...jsonResponse, status: response.status, }
    error(jsonResponseWithErrorStatus)
  } else {
    const contentType = String(response.headers.get('content-type'))
    if (contentType.startsWith('application/json;')) {
      return jsonResponse
    }
    return textResponse
  }
}

// Build a fully-qualified URL if we're only passed a path
function fullyQualifiedUrl(url) {
  // solution from here: https://stackoverflow.com/questions/10687099/how-to-test-if-a-url-string-is-absolute-or-relative
  const fullUrlTest = new RegExp('^(?:[a-z+]+:)?//', 'i');
  if (fullUrlTest.test(url)) {
    return url;
  }
  return `${window.postbuildEnv.DEFAULT_URL}${url}`;
}

function addCsrfHeaders(headers = {}) {
  const csrfElement = document.querySelector("meta[name='csrf-token']");
  const csrfToken = csrfElement ? csrfElement.getAttribute('content') : '';
  if (csrfToken) {
    headers['x-csrf-token'] = csrfToken;
  }
  return headers;
}

function requestGet(url: string, success?: Function, error?: Function, headers?: object) {
  return handleFetch({ url, method: 'get', success, error, headers, })
}

function requestPost(url: string, payload?: any, success?: Function, error?: Function) {
  return handleFetch({ url, method: 'post', success, error, payload, })
}

function requestPut(url: string, payload?: any, success?: Function, error?: Function, headers?: object) {
  return handleFetch({ url, method: 'put', success, error, payload, headers, })
}

function requestDelete(url: string, payload?: any, success?: Function, error?: Function) {
  return handleFetch({ url, method: 'delete', success, error, payload, })
}

export {
  requestDelete,
  requestGet,
  requestPost,
  requestPut
};
